import React from "react";
import "./reportCard.css";
import { DEFAULT_LAYOUT, STUDENT_FIELDS } from "./layoutDefaults";

/**
 * Renders ONE report card from the builder JSON (live preview or published snapshot).
 * The order / visibility / headings of blocks come from report.layout (or the `layout` prop).
 * Pure layout – nothing is calculated here.
 */
const fmt = (v) => (v === null || v === undefined || v === "" ? "" : Number.isInteger(v) ? v : Math.round(v * 100) / 100);

// 90.01–100 -> "91-100", 0–32 -> "32 & Below"
const legendRange = (g) => (Number(g.from) <= 0 ? `${Math.floor(Number(g.to))} & Below` : `${Math.ceil(Number(g.from))}-${Math.floor(Number(g.to))}`);

/* ------------------------------------------------------------------ blocks */

const HeaderBlock = ({ report, school, settings, block = {} }) => {
  const { student = {}, columns = [] } = report;
  const autoTitle = report.report_type === "EXAM_GROUP" ? `${columns[0]?.name || ""} Report Card` : "Annual Report Card";
  const titleLine = `${settings.title || autoTitle}${student.session_year ? ` · Session ${student.session_year}` : ""}`;
  // uploaded letterhead image replaces school name / address / logo
  if (block.mode === "IMAGE" && block.image) {
    return (
      <div className="rc-head-image">
        <img src={block.image} alt="" style={{ maxHeight: `${block.image_height || 120}px` }} />
        {block.show_title !== false && <div className="rc-title">{titleLine}</div>}
      </div>
    );
  }
  return (
    <div className="rc-head">
      {settings.show_logo && school.logo && (
        <img src={school.logo} alt="" className="rc-logo" onError={(e) => { e.target.style.display = "none"; }} />
      )}
      <div className="rc-head-text">
        {school.name && <div className="rc-school">{school.name}</div>}
        {settings.show_school_address && school.address && <div className="rc-address">{school.address}</div>}
        <div className="rc-title">
          {settings.title || autoTitle}{student.session_year ? ` · Session ${student.session_year}` : ""}
        </div>
      </div>
    </div>
  );
};

const StudentInfoBlock = ({ report, block }) => {
  const st = report.student || {};
  const values = {
    student_name: st.student_name,
    class: `${st.class_name || ""}${st.section_name ? ` - ${st.section_name}` : ""}`,
    father_name: st.father_name,
    mother_name: st.mother_name,
    sr_no: st.sr_no,
    roll_no: st.roll_no,
    date_of_birth: st.date_of_birth,
  };
  const keys = (block.fields && block.fields.length ? block.fields : DEFAULT_LAYOUT.blocks[1].fields)
    .filter((k) => STUDENT_FIELDS.some((f) => f.key === k));
  const pairs = keys.map((k) => [STUDENT_FIELDS.find((f) => f.key === k).label, values[k]]);
  const rows = [];
  for (let i = 0; i < pairs.length; i += 2) rows.push([pairs[i], pairs[i + 1]]);
  return (
    <>
      {block.title && <div className="rc-block-title">{block.title}</div>}
      <table className="rc-info">
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <th>{r[0][0]}</th><td>{r[0][1]}</td>
              <th>{r[1]?.[0] || ""}</th><td>{r[1] ? r[1][1] : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

const ScholasticBlock = ({ report, block }) => {
  const { columns = [], scholastic = {} } = report;
  const subjects = scholastic.subjects || [];
  const totals = scholastic.totals || { columns: {}, grand_total: {} };
  const showTP = block.show_theory_practical !== false;
  const showGT = block.show_grand_total !== false && columns.length > 1;

  const hasPart = (k, part) => subjects.some((s) => s.columns?.[k]?.[`has_${part}`]);

  /**
   * Sub-columns ("parts") of a term column. Every part prints Max + Obt.
   *  - term with several exams  -> one part per exam (Act.-I, Half Yearly …)
   *  - one exam, theory+practical -> Theory part + Practical part
   *  - one exam, one kind          -> no parts (Total says it all)
   */
  const hasTypes = Array.isArray(scholastic.exam_types);

  /** exam-type parts of one "place" (a column, or one exam title inside a column) */
  const typeParts = (c, pick, seg) => {
    if (!hasTypes || !showTP) return null;
    const used = scholastic.exam_types.filter((t) => subjects.some((s) => pick(s.columns?.[c.key])?.parts?.[t.key]));
    const hasGrade = used.some((t) => t.marking === "GRADE");
    if (used.length < 2 && !hasGrade) return null;
    return used.map((t) => ({
      id: `${seg.id}:${t.key}`,
      seg,
      label: t.name,
      grade: t.marking === "GRADE",
      cell: (x) => pick(x)?.parts?.[t.key],
      absent: (x) => !!pick(x)?.parts?.[t.key]?.absent,
      has: (x) => !!pick(x)?.parts?.[t.key],
      total: () => (t.marking === "GRADE" ? null : subjects.reduce((a, s) => {
        const v = pick(s.columns?.[c.key])?.parts?.[t.key];
        if (v) { a.max += v.max; a.obt += v.obt; }
        return a;
      }, { max: 0, obt: 0 })),
    }));
  };

  /**
   * Sub-columns ("parts") of a report column. Every marks part prints Max + Obt, a grade part one cell.
   *  - term with several exams -> per exam: its types (Theory / Practical / Notebook …) or just its total
   *  - one exam with types      -> one part per type
   *  - one exam, one kind       -> no parts (Total says it all)
   * part.seg = the exam title the part belongs to ({ id, name, typed })
   */
  const partsOf = (c) => {
    if (c.exam_titles.length > 1) {
      return c.exam_titles.flatMap((t) => {
        const seg = { id: t.id, name: t.name, typed: false };
        const typed = typeParts(c, (x) => x?.titles?.[t.id], seg);
        if (typed) { seg.typed = true; return typed; }
        return [{
          id: t.id,
          seg,
          label: t.name,
          cell: (x) => x?.titles?.[t.id]?.total,
          absent: (x) => x?.titles?.[t.id]?.absent,
          has: (x) => !!x?.titles?.[t.id],
          total: () => totals.columns?.[c.key]?.titles?.[t.id],
        }];
      });
    }
    if (!showTP) return [];
    const t0 = c.exam_titles[0] || {};
    if (hasTypes) {
      const seg = { id: t0.id || c.key, name: t0.name || "", typed: true };
      return typeParts(c, (x) => x, seg) || [];
    }
    // cards published before exam types: theory / practical
    const kinds = ["theory", "practical"].filter((p) => hasPart(c.key, p));
    if (kinds.length < 2) return [];
    const title = t0.name || "";
    return kinds.map((p) => ({
      id: p,
      label: `${title} ${p === "theory" ? "Theory" : "Practical"}`.trim(),
      cell: (x) => x?.[p],
      absent: () => false,
      has: (x) => !!x?.[`has_${p}`],
      total: () => {
        const r = subjects.reduce((a, s) => {
          const v = s.columns?.[c.key];
          if (v?.[`has_${p}`]) { a.max += v[p].max; a.obt += v[p].obt; }
          return a;
        }, { max: 0, obt: 0 });
        return r;
      },
    }));
  };

  const width = (parts) => parts.reduce((a, p) => a + (p.grade ? 1 : 2), 0);
  const cols = columns.map((c) => {
    const parts = partsOf(c);
    // segments = parts grouped by exam title
    const segs = [];
    parts.forEach((p) => {
      const last = segs[segs.length - 1];
      if (p.seg && last && last.seg === p.seg) last.parts.push(p);
      else segs.push({ seg: p.seg, parts: [p] });
    });
    const multi = c.exam_titles.length > 1;
    const single = !multi && parts.length > 0 && parts[0].seg?.typed;
    // an extra header row with the exam title is needed when a title has type columns under it
    // (several exams: always; one exam: only when the column name is a group name, not the exam name)
    const titleRow = multi ? segs.some((g) => g.seg?.typed) : single && parts[0].seg.name && parts[0].seg.name !== c.name;
    return { ...c, parts, segs, multi, titleRow };
  });
  const X = cols.some((c) => c.titleRow) ? 1 : 0;
  const totalLabel = (c) => (c.parts.length ? "Total" : c.exam_titles.length === 1 ? c.exam_titles[0].name : "Total");
  const obtCell = (v, absent) => (v ? (absent && !v.obt ? "AB" : fmt(v.obt)) : "");

  return (
    <>
      <table className="rc-grid rc-marks">
        <thead>
          <tr>
            <th rowSpan={3 + X} className="rc-left rc-subject-head">{block.title || "Scholastic Area"}<br /><span className="rc-sub">Subject Name</span></th>
            {cols.map((c) => (
              <th key={c.key} colSpan={width(c.parts) + 3} className="rc-gstart rc-term">{c.name}</th>
            ))}
            {showGT && <th colSpan={3} rowSpan={2 + X} className="rc-gstart rc-term">Grand Total</th>}
          </tr>
          {X === 1 && (
            <tr>
              {cols.map((c) => {
                if (!c.titleRow) return null;
                if (!c.multi) {
                  return (
                    <React.Fragment key={c.key}>
                      <th colSpan={width(c.parts) + 2} className="rc-gstart rc-strong">{c.parts[0].seg.name}</th>
                      <th rowSpan={3} className="rc-grade-head">Grade</th>
                    </React.Fragment>
                  );
                }
                return (
                  <React.Fragment key={c.key}>
                    {c.segs.map((g, i) => (g.seg?.typed ? (
                      <th key={g.seg.id} colSpan={width(g.parts)} className={`rc-strong${i === 0 ? " rc-gstart" : ""}`}>{g.seg.name}</th>
                    ) : (
                      <th key={g.seg.id} colSpan={2} rowSpan={2} className={i === 0 ? "rc-gstart" : ""}>{g.seg.name}</th>
                    )))}
                    <th colSpan={2} rowSpan={2} className="rc-strong">Total</th>
                    <th rowSpan={3} className="rc-grade-head">Grade</th>
                  </React.Fragment>
                );
              })}
            </tr>
          )}
          <tr>
            {cols.map((c) => {
              if (c.titleRow && c.multi) {
                // type names under their exam title (untyped exams / Total already span this row)
                return (
                  <React.Fragment key={c.key}>
                    {c.segs.filter((g) => g.seg?.typed).flatMap((g) => g.parts).map((p) => (
                      <th key={p.id} colSpan={p.grade ? 1 : 2} rowSpan={p.grade ? 2 : 1}>{p.label}</th>
                    ))}
                  </React.Fragment>
                );
              }
              const up = X && !c.titleRow ? 1 : 0; // columns without a title row fill that row
              return (
                <React.Fragment key={c.key}>
                  {c.parts.map((p, i) => <th key={p.id} colSpan={p.grade ? 1 : 2} rowSpan={(p.grade ? 2 : 1) + up} className={i === 0 ? "rc-gstart" : ""}>{p.label}</th>)}
                  <th colSpan={2} rowSpan={1 + up} className={c.parts.length ? "rc-strong" : "rc-gstart rc-strong"}>{totalLabel(c)}</th>
                  {!c.titleRow && <th rowSpan={2 + up} className="rc-grade-head">Grade</th>}
                </React.Fragment>
              );
            })}
          </tr>
          <tr>
            {cols.map((c) => (
              <React.Fragment key={c.key}>
                {c.parts.map((p, i) => (p.grade ? null : (
                  <React.Fragment key={p.id}>
                    <th className={`rc-mo${i === 0 ? " rc-gstart" : ""}`}>Max</th>
                    <th className="rc-mo">Obt.</th>
                  </React.Fragment>
                )))}
                <th className={`rc-mo${c.parts.length ? "" : " rc-gstart"}`}>Max</th>
                <th className="rc-mo">Obt.</th>
              </React.Fragment>
            ))}
            {showGT && <><th className="rc-mo rc-gstart">Max</th><th className="rc-mo">Obt.</th><th className="rc-grade-head">Grade</th></>}
          </tr>
        </thead>
        <tbody>
          {subjects.map((s) => (
            <tr key={s.name}>
              <td className="rc-left rc-subject">{s.name}</td>
              {cols.map((c) => {
                const x = s.columns?.[c.key];
                return (
                  <React.Fragment key={c.key}>
                    {c.parts.map((p, i) => {
                      const v = p.has(x) ? p.cell(x) : null;
                      if (p.grade) return <td key={p.id} className={`rc-grade${i === 0 ? " rc-gstart" : ""}`}>{v ? v.grade || "" : "–"}</td>;
                      return (
                        <React.Fragment key={p.id}>
                          <td className={`rc-max${i === 0 ? " rc-gstart" : ""}`}>{v ? fmt(v.max) : "–"}</td>
                          <td>{v ? obtCell(v, p.absent(x)) : "–"}</td>
                        </React.Fragment>
                      );
                    })}
                    <td className={`rc-max${c.parts.length ? "" : " rc-gstart"}`}>{x ? fmt(x.total.max) : "–"}</td>
                    <td className="rc-strong">{x ? obtCell(x.total, x.absent) : "–"}</td>
                    <td className="rc-grade">{x?.grade || ""}</td>
                  </React.Fragment>
                );
              })}
              {showGT && (
                <>
                  <td className="rc-max rc-gstart">{s.grand_total ? fmt(s.grand_total.total.max) : ""}</td>
                  <td className="rc-strong">{s.grand_total ? fmt(s.grand_total.total.obt) : ""}</td>
                  <td className="rc-grade">{s.grand_total?.grade || ""}</td>
                </>
              )}
            </tr>
          ))}
          <tr className="rc-total">
            <td className="rc-left">Total</td>
            {cols.map((c) => {
              const t = totals.columns?.[c.key];
              return (
                <React.Fragment key={c.key}>
                  {c.parts.map((p, i) => {
                    const pt = p.total();
                    if (p.grade) return <td key={p.id} className={i === 0 ? "rc-gstart" : ""}></td>;
                    return (
                      <React.Fragment key={p.id}>
                        <td className={i === 0 ? "rc-gstart" : ""}>{pt ? fmt(pt.max) : ""}</td>
                        <td>{pt ? fmt(pt.obt) : ""}</td>
                      </React.Fragment>
                    );
                  })}
                  <td className={c.parts.length ? "" : "rc-gstart"}>{t ? fmt(t.max) : ""}</td>
                  <td>{t ? fmt(t.obt) : ""}</td>
                  <td className="rc-grade">{t?.grade || ""}</td>
                </React.Fragment>
              );
            })}
            {showGT && (
              <>
                <td className="rc-gstart">{fmt(totals.grand_total?.max)}</td>
                <td>{fmt(totals.grand_total?.obt)}</td>
                <td className="rc-grade">{totals.grand_total?.grade || ""}</td>
              </>
            )}
          </tr>
        </tbody>
      </table>

      {(block.show_percentage !== false || block.show_result !== false) && totals.grand_total?.percentage != null && (
        <div className="rc-summary-boxes">
          <div className="rc-box"><span>Marks Obtained</span><b>{fmt(totals.grand_total.obt)} / {fmt(totals.grand_total.max)}</b></div>
          {block.show_percentage !== false && <div className="rc-box"><span>Percentage</span><b>{fmt(totals.grand_total.percentage)}%</b></div>}
          {totals.grand_total.grade && <div className="rc-box"><span>Overall Grade</span><b>{totals.grand_total.grade}</b></div>}
          {block.show_result !== false && report.result_status && (
            <div className={`rc-box rc-result-${String(report.result_status).toLowerCase()}`}><span>Result</span><b>{report.result_status}</b></div>
          )}
        </div>
      )}
    </>
  );
};

const CoScholasticBlock = ({ report, block }) => {
  const co = report.co_scholastic || {};
  if (!(co.subjects || []).length) return null;
  return (
    <table className="rc-grid rc-cosch">
      <thead>
        <tr>
          <th className="rc-left">{block.title || "Co-Scholastic Area"}</th>
          {(co.columns || []).map((c) => <th key={c.key}>{c.name}<br />Grade</th>)}
        </tr>
      </thead>
      <tbody>
        {co.subjects.map((s) => (
          <tr key={s.id}>
            <td className="rc-left">{s.name}</td>
            {(co.columns || []).map((c) => <td key={c.key}><b>{s.grades?.[c.key] || ""}</b></td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const FooterBlock = ({ report, block }) => {
  const footer = report.footer || [];
  if (!footer.length) return null;
  return (
    <>
      {block.title && <div className="rc-block-title">{block.title}</div>}
      <table className="rc-footer">
        <tbody>
          {footer.map((f) => (
            <tr key={f.field_key}>
              <th>{f.title}</th>
              <td className={f.input_type === "TEXTAREA" ? "rc-remark" : ""}>{f.value ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

const SignaturesBlock = ({ report, block }) => {
  const labels = (block.labels || []).filter((l) => l !== undefined);
  const date = report.issue_date ? new Date(report.issue_date).toLocaleDateString("en-GB") : "";
  return (
    <div className="rc-sign">
      {labels.map((l, i) => (
        <div key={i}>{/^date$/i.test(l.trim()) ? `Date: ${date}` : l}</div>
      ))}
    </div>
  );
};

const LegendBlock = ({ report, block }) => {
  const gl = report.grade_legend || {};
  const showS = block.show_scholastic !== false && (gl.scholastic || []).length > 0;
  const showC = block.show_co_scholastic !== false && (gl.co_scholastic || []).length > 0;
  if (!showS && !showC && !block.title) return null;
  return (
    <div className="rc-legend">
      {block.title && <div className="rc-legend-title">{block.title}</div>}
      <div className="rc-legend-tables">
        {showS && (
          <table>
            <thead><tr><th colSpan={2}>Scholastic</th></tr><tr><th>Marks Range</th><th>Grade</th></tr></thead>
            <tbody>{gl.scholastic.map((g) => <tr key={g.grade}><td>{legendRange(g)}</td><td>{g.grade}</td></tr>)}</tbody>
          </table>
        )}
        {showC && (
          <table>
            <thead><tr><th colSpan={2}>Co-Scholastic</th></tr><tr><th>Marks Range</th><th>Grade</th></tr></thead>
            <tbody>{gl.co_scholastic.map((g) => <tr key={g.grade}><td>{legendRange(g)}</td><td>{g.grade}</td></tr>)}</tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const TextBlock = ({ block }) =>
  block.text ? (
    <div className="rc-text" style={{ textAlign: block.align || "left", fontWeight: block.bold ? 600 : 400 }}>
      {block.text}
    </div>
  ) : null;

const SpacerBlock = ({ block }) => <div style={{ height: `${block.height || 10}px` }} />;

const RENDERERS = {
  HEADER: HeaderBlock,
  STUDENT_INFO: StudentInfoBlock,
  SCHOLASTIC: ScholasticBlock,
  CO_SCHOLASTIC: CoScholasticBlock,
  FOOTER: FooterBlock,
  SIGNATURES: SignaturesBlock,
  GRADE_LEGEND: LegendBlock,
  TEXT: TextBlock,
  SPACER: SpacerBlock,
};

/* ------------------------------------------------------------------ card */

const ReportCardView = ({ report, school = {}, layout: layoutProp, highlightId }) => {
  if (!report) return null;
  const layout = layoutProp || report.layout || DEFAULT_LAYOUT;
  const settings = { ...DEFAULT_LAYOUT.settings, ...(layout.settings || {}) };

  return (
    <div className="rc-page" style={{ fontSize: `${settings.font_size || 11}px` }}>
      {(layout.blocks || []).filter((b) => b.visible !== false).map((b) => {
        const R = RENDERERS[b.type];
        if (!R) return null;
        return (
          <div key={b.id} className={`rc-block${highlightId === b.id ? " rc-highlight" : ""}`}>
            <R report={report} school={school} settings={settings} block={b} />
          </div>
        );
      })}
      {report.version && <div className="rc-version">v{report.version}</div>}
    </div>
  );
};

export default ReportCardView;
